import React, { useState, useEffect, useCallback } from "react";
import globals from '../../../Globals';
import { ShoppingBag, RefreshCw, Search, Eye, X } from 'lucide-react';

const API = globals.API;

// Simplified for the current phase: there is no payment yet, so every order that comes
// in through the partner integration is just a "nouvelle demande" waiting to be handled
// by the Aviator team. No status filter / status badges — that comes back once a real
// payment phase is added later (the backend still tracks a status field for that).
const DashboardOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [detailsModal, setDetailsModal] = useState({ show: false, order: null });

  const token = localStorage.getItem('authToken');

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const fetchOrders = useCallback(async () => {
    if (!token) {
      showMessage('error', 'Authentification requise. Veuillez vous reconnecter.');
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      params.set('limit', '100');

      const response = await fetch(`${API}/orders?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        showMessage('error', 'Session expirée. Veuillez vous reconnecter.');
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setOrders(data.data || []);
      } else {
        showMessage('error', data.message || 'Échec du chargement des commandes.');
      }
    } catch (error) {
      showMessage('error', 'Erreur réseau : ' + error.message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const styles = {
    container: { minHeight: '100vh', backgroundColor: '#f9fafb', padding: '24px' },
    maxWidth: { maxWidth: '1280px', margin: '0 auto' },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      padding: '24px',
      marginBottom: '24px',
    },
    header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' },
    titleSection: { display: 'flex', alignItems: 'center', gap: '12px' },
    title: { fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 },
    subtitle: { color: '#6b7280', fontSize: '14px', margin: 0 },
    button: {
      display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px',
      backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px',
      cursor: 'pointer', fontSize: '14px', fontWeight: '500',
    },
    totalPill: {
      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px',
      backgroundColor: '#dbeafe', borderRadius: '8px', width: 'fit-content',
    },
    totalPillLabel: { fontSize: '13px', fontWeight: '500', color: '#2563eb', margin: 0 },
    totalPillValue: { fontSize: '22px', fontWeight: 'bold', color: '#1e3a8a', margin: 0 },
    alert: { padding: '16px', borderRadius: '8px', marginBottom: '24px' },
    filterSection: { display: 'flex', alignItems: 'center', gap: '16px' },
    searchBox: { display: 'flex', alignItems: 'center', gap: '8px', flex: 1 },
    input: { flex: 1, border: 'none', outline: 'none', fontSize: '14px', color: '#374151' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: {
      padding: '12px 24px', textAlign: 'left', fontSize: '12px', fontWeight: '500',
      color: '#6b7280', textTransform: 'uppercase', backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
    },
    td: { padding: '16px 24px', borderBottom: '1px solid #e5e7eb' },
    sourceBadge: {
      padding: '4px 10px', fontSize: '11px', fontWeight: '700',
      borderRadius: '6px', display: 'inline-block',
      backgroundColor: '#ede9fe', color: '#5b21b6', letterSpacing: '0.3px',
    },
    viewBtn: {
      padding: '8px', backgroundColor: 'transparent', color: '#2563eb',
      border: 'none', borderRadius: '6px', cursor: 'pointer',
    },
    loading: { padding: '48px', textAlign: 'center', color: '#6b7280' },
    modal: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '16px', zIndex: 50, overflowY: 'auto',
    },
    modalContent: {
      backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 20px 25px rgba(0,0,0,0.15)',
      maxWidth: '650px', width: '100%', padding: '24px', maxHeight: '90vh',
      overflowY: 'auto', margin: '20px 0',
    },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
    modalTitle: { fontSize: '20px', fontWeight: 'bold', color: '#111827', margin: 0 },
    closeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280' },
    detailSection: { marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #e5e7eb' },
    detailSectionTitle: { fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px' },
    detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' },
    detailItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
    detailLabel: { fontSize: '12px', fontWeight: '500', color: '#6b7280', textTransform: 'uppercase' },
    detailValue: { fontSize: '14px', color: '#111827', fontWeight: '500' },
  };

  return (
    <div style={styles.container}>
      <div style={styles.maxWidth}>
        {/* Header */}
        <div style={styles.card}>
          <div style={styles.header}>
            <div style={styles.titleSection}>
              <ShoppingBag color="#2563eb" size={32} />
              <div>
                <h1 style={styles.title}>Commandes partenaires</h1>
                <p style={styles.subtitle}>Nouvelles demandes reçues via l'intégration My Tarraji (IZI) — à traiter par l'équipe Aviator</p>
              </div>
            </div>
            <button onClick={fetchOrders} style={styles.button}>
              <RefreshCw size={16} />
              Actualiser
            </button>
          </div>

          <div style={styles.totalPill}>
            <p style={styles.totalPillLabel}>Total reçues</p>
            <p style={styles.totalPillValue}>{orders.length}</p>
          </div>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div style={{
            ...styles.alert,
            backgroundColor: message.type === 'success' ? '#dcfce7' : '#fee2e2',
            color: message.type === 'success' ? '#166534' : '#991b1b',
          }}>
            {message.text}
          </div>
        )}

        {/* Search */}
        <div style={styles.card}>
          <div style={styles.filterSection}>
            <div style={styles.searchBox}>
              <Search color="#9ca3af" size={20} />
              <input
                type="text"
                placeholder="Rechercher par référence, client, voyage..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.input}
              />
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div style={{ ...styles.card, padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={styles.loading}>Chargement des commandes...</div>
          ) : orders.length === 0 ? (
            <div style={styles.loading}>Aucune commande pour le moment</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Référence</th>
                  <th style={styles.th}>Source</th>
                  <th style={styles.th}>Client</th>
                  <th style={styles.th}>Voyage</th>
                  <th style={styles.th}>Montant</th>
                  <th style={styles.th}>Reçue le</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order._id} style={{ backgroundColor: 'white' }}>
                    <td style={styles.td}>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {order.orderRef}
                      </div>
                      <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                        {order.partnerTransactionId || '—'}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={styles.sourceBadge}>MY TARRAJI</span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                        {order.customer?.nom || 'N/A'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>
                        {order.customer?.email || order.customer?.telephone || ''}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: '14px', color: '#374151' }}>
                        {order.eventTitle || order.event?.title || 'N/A'}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>
                        {order.amount != null ? `${order.amount} ${order.currency || 'TND'}` : 'N/A'}
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>{formatDate(order.createdAt)}</div>
                    </td>
                    <td style={styles.td}>
                      <button
                        style={styles.viewBtn}
                        onClick={() => setDetailsModal({ show: true, order })}
                        title="Voir le détail"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {detailsModal.show && detailsModal.order && (
        <div style={styles.modal} onClick={() => setDetailsModal({ show: false, order: null })}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Commande {detailsModal.order.orderRef}</h2>
              <button style={styles.closeBtn} onClick={() => setDetailsModal({ show: false, order: null })}>
                <X size={22} />
              </button>
            </div>

            <div style={styles.detailSection}>
              <h3 style={styles.detailSectionTitle}>Commande</h3>
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Source</span>
                  <span style={styles.detailValue}>My Tarraji (IZI)</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Référence partenaire</span>
                  <span style={styles.detailValue}>{detailsModal.order.partnerTransactionId || 'N/A'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Montant indicatif</span>
                  <span style={styles.detailValue}>
                    {detailsModal.order.amount != null ? `${detailsModal.order.amount} ${detailsModal.order.currency || 'TND'}` : 'N/A'}
                  </span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Voyage</span>
                  <span style={styles.detailValue}>{detailsModal.order.eventTitle || detailsModal.order.event?.title || 'N/A'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Ville de départ</span>
                  <span style={styles.detailValue}>{detailsModal.order.departureCity || '-'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Date de départ</span>
                  <span style={styles.detailValue}>{formatDate(detailsModal.order.departureDate)}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Date de retour</span>
                  <span style={styles.detailValue}>{formatDate(detailsModal.order.returnDate)}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Voyageurs</span>
                  <span style={styles.detailValue}>{detailsModal.order.travelers || '-'}</span>
                </div>
                <div style={{ ...styles.detailItem, gridColumn: '1 / -1' }}>
                  <span style={styles.detailLabel}>Chambres (adultes, enfants, bébés)</span>
                  <span style={{ ...styles.detailValue, whiteSpace: 'pre-line' }}>{detailsModal.order.rooms || '-'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Reçue le</span>
                  <span style={styles.detailValue}>{formatDate(detailsModal.order.createdAt)}</span>
                </div>
              </div>
            </div>

            <div style={styles.detailSection}>
              <h3 style={styles.detailSectionTitle}>Client</h3>
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Nom</span>
                  <span style={styles.detailValue}>{detailsModal.order.customer?.nom || 'N/A'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Email</span>
                  <span style={styles.detailValue}>{detailsModal.order.customer?.email || 'N/A'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Téléphone</span>
                  <span style={styles.detailValue}>{detailsModal.order.customer?.telephone || '-'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>WhatsApp</span>
                  <span style={styles.detailValue}>{detailsModal.order.customer?.whatsapp || '-'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Adresse</span>
                  <span style={styles.detailValue}>{detailsModal.order.customer?.adresse || '-'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Ville</span>
                  <span style={styles.detailValue}>{detailsModal.order.customer?.ville || '-'}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Mode de paiement</span>
                  <span style={styles.detailValue}>{detailsModal.order.modePaiement || '-'}</span>
                </div>
                {detailsModal.order.remarque && (
                  <div style={{ ...styles.detailItem, gridColumn: '1 / -1' }}>
                    <span style={styles.detailLabel}>Remarque</span>
                    <span style={styles.detailValue}>{detailsModal.order.remarque}</span>
                  </div>
                )}
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardOrders;
